const STORAGE_KEY = 'pq-monitor:reload-scroll:v1';
const QUIET_MS = 250;
const MAX_IDLE_RESTORE_MS = 15_000;
const POSITION_TOLERANCE_PX = 1;

interface PlotAnchor {
  plotId: string;
  plotIndex: number;
  offsetY: number;
}

interface SavedScrollPosition {
  version: 1;
  href: string;
  x: number;
  y: number;
  anchor?: PlotAnchor;
}

export interface ReloadScrollRestorer {
  setEnabled(enabled: boolean): void;
  setRequestsActive(active: boolean): void;
  destroy(): void;
}

const MODIFIER_KEYS = new Set([
  'Alt',
  'CapsLock',
  'Control',
  'Meta',
  'NumLock',
  'ScrollLock',
  'Shift'
]);

function currentNavigationIsReload(): boolean {
  try {
    const entry = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (entry) return entry.type === 'reload';
    const legacy = (performance as Performance & { navigation?: { type?: number } }).navigation;
    return legacy?.type === 1;
  } catch {
    return false;
  }
}

function readSavedPosition(): SavedScrollPosition | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<SavedScrollPosition>;
    if (
      value.version !== 1
      || typeof value.href !== 'string'
      || !Number.isFinite(value.x)
      || !Number.isFinite(value.y)
      || (value.x as number) < 0
      || (value.y as number) < 0
    ) return null;
    if (value.anchor != null && (
      typeof value.anchor.plotId !== 'string'
      || value.anchor.plotId.length === 0
      || !Number.isSafeInteger(value.anchor.plotIndex)
      || value.anchor.plotIndex < 0
      || !Number.isFinite(value.anchor.offsetY)
      || value.anchor.offsetY < 0
    )) return null;
    return value as SavedScrollPosition;
  } catch {
    return null;
  }
}

function findPlotAnchor(): PlotAnchor | undefined {
  let nearest: HTMLElement | null = null;
  let nearestIndex = -1;
  const plots = document.querySelectorAll<HTMLElement>('[data-plot-id]');
  for (const [index, element] of Array.from(plots).entries()) {
    const plotId = element.dataset.plotId;
    if (!plotId) continue;
    const bounds = element.getBoundingClientRect();
    if (bounds.top <= POSITION_TOLERANCE_PX) {
      nearest = element;
      nearestIndex = index;
      if (bounds.bottom >= -POSITION_TOLERANCE_PX) break;
    } else {
      break;
    }
  }
  if (!nearest?.dataset.plotId) return undefined;
  const offsetY = -nearest.getBoundingClientRect().top;
  if (!Number.isFinite(offsetY) || offsetY < 0) return undefined;
  return { plotId: nearest.dataset.plotId, plotIndex: nearestIndex, offsetY };
}

function writeSavedPosition(saved: SavedScrollPosition): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
  } catch {
    // Scroll restoration is optional when browser storage is unavailable.
  }
}

function saveCurrentPosition(): void {
  const x = Math.max(0, window.scrollX || window.pageXOffset || 0);
  const y = Math.max(0, window.scrollY || window.pageYOffset || 0);
  const saved: SavedScrollPosition = {
    version: 1,
    href: window.location.href,
    x,
    y
  };
  const anchor = findPlotAnchor();
  if (anchor) saved.anchor = anchor;
  writeSavedPosition(saved);
}

function documentHeight(): number {
  return Math.max(
    document.documentElement?.scrollHeight ?? 0,
    document.body?.scrollHeight ?? 0
  );
}

/**
 * Repairs the browser's reload scroll restoration after the asynchronous
 * workspace has made the original document position reachable again.
 */
export function createReloadScrollRestorer(): ReloadScrollRestorer {
  let destroyed = false;
  let enabled = true;
  let restoring = false;
  let requestsActive = true;
  let frameId: number | null = null;
  let quietSince: number | null = null;
  let idleSince: number | null = null;
  let lastHeight = -1;
  let lastTargetY = -1;
  let anchorElement: HTMLElement | null = null;
  let resizeObserver: ResizeObserver | null = null;
  let savedHistoryMode: ScrollRestoration | null = null;

  const saved = currentNavigationIsReload() ? readSavedPosition() : null;
  const matchingSaved = saved?.href === window.location.href ? saved : null;

  function restoreHistoryMode() {
    if (savedHistoryMode == null || !('scrollRestoration' in history)) return;
    try { history.scrollRestoration = savedHistoryMode; } catch { /* older browsers may reject it */ }
    savedHistoryMode = null;
  }

  function removeCancellationListeners() {
    window.removeEventListener('wheel', cancelForUserInput);
    window.removeEventListener('touchstart', cancelForUserInput);
    window.removeEventListener('pointerdown', cancelForUserInput);
    window.removeEventListener('keydown', cancelForKeyboardInput);
    window.removeEventListener('focusin', cancelForUserInput);
  }

  function finishRestoration() {
    if (!restoring) return;
    restoring = false;
    if (frameId != null) window.cancelAnimationFrame(frameId);
    frameId = null;
    resizeObserver?.disconnect();
    resizeObserver = null;
    removeCancellationListeners();
    restoreHistoryMode();
  }

  function cancelForUserInput() {
    finishRestoration();
  }

  function cancelForKeyboardInput(event: KeyboardEvent) {
    const refreshShortcut = event.key === 'F5'
      || ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'r');
    if (!refreshShortcut && !MODIFIER_KEYS.has(event.key)) finishRestoration();
  }

  function scheduleFrame() {
    if (!restoring || frameId != null) return;
    frameId = window.requestAnimationFrame(restoreFrame);
  }

  function resolveTargetY(savedPosition: SavedScrollPosition): number {
    const anchor = savedPosition.anchor;
    if (!anchor) return savedPosition.y;
    if (!anchorElement || !document.documentElement.contains(anchorElement)) {
      const plots = Array.from(document.querySelectorAll<HTMLElement>('[data-plot-id]'));
      anchorElement = plots.find((element) => element.dataset.plotId === anchor.plotId)
        ?? plots[anchor.plotIndex]
        ?? null;
    }
    if (!anchorElement) return savedPosition.y;
    const documentTop = window.scrollY + anchorElement.getBoundingClientRect().top;
    const anchoredTarget = documentTop + anchor.offsetY;
    return Number.isFinite(anchoredTarget) ? Math.max(0, anchoredTarget) : savedPosition.y;
  }

  function restoreFrame(timestamp: number) {
    frameId = null;
    if (!restoring || !matchingSaved) return;

    const height = documentHeight();
    const targetY = resolveTargetY(matchingSaved);
    const viewportHeight = Math.max(window.innerHeight || 0, document.documentElement?.clientHeight ?? 0);
    const reachableY = Math.min(targetY, Math.max(0, height - viewportHeight));

    if (
      Math.abs(window.scrollX - matchingSaved.x) > POSITION_TOLERANCE_PX
      || Math.abs(window.scrollY - reachableY) > POSITION_TOLERANCE_PX
    ) {
      window.scrollTo({ left: matchingSaved.x, top: targetY, behavior: 'auto' });
    }

    const targetIsReachable = targetY <= Math.max(0, height - viewportHeight) + POSITION_TOLERANCE_PX;
    const reached = targetIsReachable && Math.abs(window.scrollY - targetY) <= POSITION_TOLERANCE_PX;
    const layoutStable = height === lastHeight && Math.abs(targetY - lastTargetY) <= POSITION_TOLERANCE_PX;
    if (requestsActive) {
      quietSince = null;
      idleSince = null;
    } else {
      if (idleSince == null) idleSince = timestamp;
      quietSince = reached && layoutStable ? (quietSince ?? timestamp) : null;
    }
    lastHeight = height;
    lastTargetY = targetY;

    const quietlySettled = quietSince != null && timestamp - quietSince >= QUIET_MS;
    const idleTimedOut = idleSince != null && timestamp - idleSince >= MAX_IDLE_RESTORE_MS;
    if (quietlySettled || idleTimedOut) {
      finishRestoration();
      return;
    }
    scheduleFrame();
  }

  function captureOnPageHide() {
    if (!enabled) {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* storage can be disabled */ }
    } else if (restoring && matchingSaved) {
      // A second refresh during a clamped restore should retain the original target.
      writeSavedPosition(matchingSaved);
    } else {
      saveCurrentPosition();
    }
    finishRestoration();
    restoreHistoryMode();
  }

  window.addEventListener('pagehide', captureOnPageHide);

  if (matchingSaved) {
    restoring = true;
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* storage can be disabled */ }
    if ('scrollRestoration' in history) {
      try {
        savedHistoryMode = history.scrollRestoration;
        history.scrollRestoration = 'manual';
      } catch {
        savedHistoryMode = null;
      }
    }
    window.addEventListener('wheel', cancelForUserInput, { passive: true });
    window.addEventListener('touchstart', cancelForUserInput, { passive: true });
    window.addEventListener('pointerdown', cancelForUserInput, { passive: true });
    window.addEventListener('keydown', cancelForKeyboardInput);
    window.addEventListener('focusin', cancelForUserInput);
    if (typeof ResizeObserver !== 'undefined') {
      try {
        resizeObserver = new ResizeObserver(scheduleFrame);
        resizeObserver.observe(document.documentElement);
        if (document.body) resizeObserver.observe(document.body);
      } catch {
        resizeObserver?.disconnect();
        resizeObserver = null;
      }
    }
    scheduleFrame();
  }

  return {
    setEnabled(nextEnabled: boolean) {
      enabled = nextEnabled;
      if (enabled) return;
      try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* storage can be disabled */ }
      finishRestoration();
    },
    setRequestsActive(active: boolean) {
      requestsActive = active;
      if (active) {
        quietSince = null;
        idleSince = null;
      }
      scheduleFrame();
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      window.removeEventListener('pagehide', captureOnPageHide);
      finishRestoration();
      restoreHistoryMode();
    }
  };
}
